import * as signalR from '@microsoft/signalr'
import keycloak from '../../config/keycloak'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface CallEndedEvent {
  callId:       string   // CRM call_logs.id (UUID)
  status:       string   // e.g. "CONNECTED", "NOT_ANSWERED"
  duration:     number   // seconds
  recordingUrl: string | null
  endedAt:      string   // ISO UTC timestamp
  agentCallStatus?: string | null
  customerCallStatus?: string | null
}

export interface MissedCallLead {
  id: string
  name: string
  status: string
  companyName?: string | null
  ownerId: string
  assignedToId?: string | null
}

export interface MissedCallDeal {
  id: string
  title: string
  stage: string
  amount?: number | null
  status: string
  ownerId: string
  assignedToId?: string | null
}

export interface MissedCallSales {
  id: string
  dealName: string
  status: string
  ownerId: string
  assignedToId?: string | null
}

export interface MissedCallRetention {
  id: string
  customerName: string
  status: string
  ownerId?: string | null
}

export interface MissedCallContact {
  id: string
  name: string
  email?: string | null
  createdById?: string | null
}

export interface MissedCallEvent {
  callId: string
  customerNumber: string
  callStatus: string
  startTime: string
  duration: string
  leads: MissedCallLead[]
  deals: MissedCallDeal[]
  sales: MissedCallSales[]
  retentionCustomers: MissedCallRetention[]
  contacts: MissedCallContact[]
}

type CallEndedHandler = (event: CallEndedEvent) => void
type MissedCallHandler = (event: MissedCallEvent) => void

// ── Hub URL ───────────────────────────────────────────────────────────────────
// In dev:  use a relative path ("/hubs/call") — Vite proxy forwards it to localhost:5275.
//          This avoids hardcoding the backend port and keeps WebSocket upgrades working.
// In prod: strip "/api/v1" from the configured API URL to get the server root.

function getHubUrl(): string {
  if (import.meta.env.DEV) {
    // Relative URL — handled by the "/hubs" proxy rule in vite.config.ts
    return '/hubs/call'
  }

  const apiUrl =
    import.meta.env.VITE_API_BASE_URL ||
    import.meta.env.VITE_API_URL ||
    ''

  // Strip trailing /api/v1 (or /api/v1/) to get the server root
  const root = apiUrl.replace(/\/api\/v1\/?$/, '').replace(/\/$/, '')
  return root ? `${root}/hubs/call` : '/hubs/call'
}

console.log('[SignalRService] Initializing signalRService module...')

// ── Singleton service ─────────────────────────────────────────────────────────

class SignalRService {
  private connection: signalR.HubConnection | null = null
  private callEndedHandlers: CallEndedHandler[] = []
  private missedCallHandlers: MissedCallHandler[] = []

  /** Start the SignalR connection. Call once after the user authenticates. */
  async connect(): Promise<void> {
    console.log('[SignalR] connect() invoked | existing connection state:', this.connection?.state)
    if (this.connection) {
      console.log('[SignalR] Already connected or connecting. Skipping connect().')
      return
    }

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(getHubUrl(), {
        // Send the Keycloak access token on every negotiation / reconnect
        accessTokenFactory: async () => {
          try {
            if (keycloak.authenticated) {
              await keycloak.updateToken(30)
            }
          } catch {
            // Token refresh failed — attempt connection with current token
          }
          return keycloak.token ?? ''
        },
        // Prefer WebSockets; fall back to SSE, then long-polling
        transport:
          signalR.HttpTransportType.WebSockets |
          signalR.HttpTransportType.ServerSentEvents |
          signalR.HttpTransportType.LongPolling,
        skipNegotiation: false,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(
        import.meta.env.DEV
          ? signalR.LogLevel.Information
          : signalR.LogLevel.Warning
      )
      .build()

    // ── Event: CallEnded ────────────────────────────────────────────────────
    this.connection.on('CallEnded', (event: CallEndedEvent) => {
      console.log('[SignalR] Event received -> CallEnded:', event)
      this.callEndedHandlers.forEach(h => {
        try {
          h(event)
        } catch (err) {
          console.error('[SignalR] Error in CallEnded handler:', err)
        }
      })
    })

    // ── Event: MissedCallReceived ───────────────────────────────────────────
    this.connection.on('MissedCallReceived', (event: MissedCallEvent) => {
      console.log('[SignalR] Event received -> MissedCallReceived:', event)
      this.missedCallHandlers.forEach(h => {
        try {
          h(event)
        } catch (err) {
          console.error('[SignalR] Error in MissedCallReceived handler:', err)
        }
      })
    })

    this.connection.onreconnecting(err => {
      console.warn('[SignalR] Connection lost. Reconnecting to CallHub…', err)
    })

    this.connection.onreconnected(connectionId => {
      console.info('[SignalR] Reconnected to CallHub successfully. Connection ID:', connectionId)
    })

    this.connection.onclose(err => {
      if (err) {
        console.error('[SignalR] Connection closed with error:', err)
      } else {
        console.info('[SignalR] Connection closed cleanly.')
      }
    })

    const hubUrl = getHubUrl()
    console.log('[SignalR] Attempting to connect to CallHub at:', hubUrl)

    try {
      await this.connection.start()
      console.info('[SignalR] ✅ Connected to CallHub at:', hubUrl, '| State:', this.connection.state);
    } catch (err) {
      console.error('[SignalR] ❌ Failed to connect to CallHub at:', hubUrl, '| Error:', err)
      // Reset so a subsequent connect() call can retry.
      this.connection = null
    }
  }

  /** Gracefully stop the connection. Call on logout. */
  async disconnect(): Promise<void> {
    if (!this.connection) return
    console.log('[SignalR] Disconnecting from CallHub…')
    try {
      await this.connection.stop()
      console.info('[SignalR] Disconnected from CallHub.')
    } catch (err) {
      console.warn('[SignalR] Error during disconnect:', err)
    } finally {
      this.connection = null
    }
  }

  /** Register a handler that fires when a CallEnded event arrives. */
  onCallEnded(handler: CallEndedHandler): () => void {
    this.callEndedHandlers.push(handler)
    // Return an unsubscribe function
    return () => {
      this.callEndedHandlers = this.callEndedHandlers.filter(h => h !== handler)
    }
  }

  /** Register a handler that fires when a MissedCallReceived event arrives. */
  onMissedCallReceived(handler: MissedCallHandler): () => void {
    this.missedCallHandlers.push(handler)
    return () => {
      this.missedCallHandlers = this.missedCallHandlers.filter(h => h !== handler)
    }
  }
}

export const signalRService = new SignalRService()
